import React, { useCallback, useMemo, useState } from 'react';

import useTranslation from '~/app/hooks/useTranslation';
import { useAppLoader } from '~/app/modules/appLoader';

import { useStoreActions, useStoreState } from '~/store';

import Item, { ItemProps } from './Item';
import ExternalLink from "~/app/common/ExternalLink";
import classes from './style.module.scss';
import { MaterialIcons } from '~/app/common/Icon';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import useAccessControl from '~/app/hooks/useAccessControl';

export type MenuComponent = React.FC

const Menu: MenuComponent = () => {
  const { t } = useTranslation('mainNav');
  const { REACT_APP_HELP_URL = '' } = process.env;

  const { showAppLoader, hideAppLoader } = useAppLoader();
  const [showUserGuideDialog, setShowUserGuideDialog] = useState<boolean>(false);

  const logoutAction = useStoreActions(actions => actions.user.logout);

  const itemLogoutClickHandler = useCallback((): void => {
    showAppLoader();

    logoutAction({ microsoftLogout: true }).finally(hideAppLoader);
  }, [hideAppLoader, logoutAction, showAppLoader]);

  const toggleShowUserGuideDialog = useCallback(() => {
    setShowUserGuideDialog(!showUserGuideDialog);
  }, [showUserGuideDialog]);
const { checkPermission } = useAccessControl();
const timeManagementPermission = checkPermission(PERMISSION_GROUPS.timeManagement);
const hasTimeforingModule = useStoreState(state => state.tenant.hasTimeforingModule);
const networkStatus = useStoreState(state => state.app.offline.status);

const firstGroup = useMemo<({ key: string } & ItemProps)[]>(() => {
  const items = [
    { key: 'home', label: t('home'), to: '/home', icon: 'home' as MaterialIcons, showArrow: true },
    ...(!networkStatus ? [{ key: 'tasks', label: t('tasks'), to: '/tasks', icon: 'assignment' as MaterialIcons, showArrow: true }] : []),
  ];

  if (hasTimeforingModule && timeManagementPermission && !networkStatus) {
    items.splice(1, 0, { 
      key: 'timeManagement', 
      label: t('timeTracking'), 
      to: '/timeManagement', 
      icon: 'schedule' as MaterialIcons, 
      showArrow: true 
    }, { 
      key: 'calendar', 
      label: t('calendar'), 
      to: '/timeManagement/calender', 
      icon: 'calendar_today' as MaterialIcons, 
      showArrow: true 
    });
  }

  return items;
}, [t, hasTimeforingModule, timeManagementPermission, networkStatus]);
  const secondGroup = useMemo<({ key: string } & ItemProps)[]>(() => ([
    { key: 'about', label: t('aboutUs'), to: '/aboutUs', icon: 'info', showArrow: true },
    { key: 'userGuide', label: t('userGuide'), to: '#', icon: 'notes', clickHandler: toggleShowUserGuideDialog, showArrow: true },
  ]), [t, toggleShowUserGuideDialog]);

  const logoutItem = useMemo<({ key: string } & ItemProps)[]>(() => ([
    { key: 'logout', label: t('signOut'), to: '#', icon: 'launch', clickHandler: itemLogoutClickHandler, itemClassName: 'font-color-red' },
  ]), [t, itemLogoutClickHandler]);

  return (
    <div>
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
      
      <div className={classes['menu-section']}>
        {firstGroup.map(item => <Item {...item} key={item.key} />)}
      </div>
      
      <div className={classes['menu-divider']} />
      
      <div className={classes['menu-section']}>
        {secondGroup.map(item => <Item {...item} key={item.key} />)}
      </div>
      
      <div className={classes['menu-divider']} />
      
      <div className={classes['menu-section']}>
        {logoutItem.map(item => <Item {...item} key={item.key} />)}
      </div>
      
      <ExternalLink open={showUserGuideDialog} onClose={toggleShowUserGuideDialog} url={REACT_APP_HELP_URL} />
    </div>
  );
};

export default Menu;